import { type CSSProperties, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '@/hooks/useBrowserOnly';
import styles from './FilmHero.module.css';

/**
 * The shop's film hero (landing, Commercial DC, Sales Partners and every
 * /why/:icp page): the film fills the hero's background, the copy sits at its
 * foot over a scrim.
 *
 * The films are portrait 9:16 (720x1280), so at full width a film is 16/9 of
 * the screen width tall and a hero one screen high shows only a thin slice of
 * it, which cut heads off (Warren, 2026-10-06: "I don't like how videos are
 * cut off"). On a phone the hero fills the first screen and shows nearly all of
 * the film. On a wide screen it runs longer than the first screen, deep enough
 * to show half of the film (the person and the charger), with the copy below
 * the film's subject (Warren: "drop the hero area lower/longer so the video
 * isn't hidden on desktop"). `framing` (object-position Y, percent) picks which
 * half shows.
 *
 * A landscape (16:9) film, such as the Sales Partner program film, runs at its
 * own height on a wide screen (9/16 of the width), so the whole frame shows;
 * on a phone or portrait tablet the hero crops its sides, and `framingX`
 * (object-position X, percent) picks which part shows.
 */
export function FilmHero({
  video, poster, framing = 40, framingX = 50, orientation = 'portrait', label, children,
}: {
  video: string;
  poster: string;
  framing?: number;
  framingX?: number;
  orientation?: 'portrait' | 'landscape';
  label: string;
  children: ReactNode;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const style = {
    '--film-framing': `${framing}%`,
    '--film-framing-x': `${framingX}%`,
  } as CSSProperties;
  const className = orientation === 'landscape' ? `${styles.hero} ${styles.landscape}` : styles.hero;
  return (
    <section className={className} style={style} aria-label={label}>
      <div className={styles.frame} aria-hidden>
        {reducedMotion ? (
          <img className={styles.film} src={poster} alt="" />
        ) : (
          <video className={styles.film} src={video} poster={poster} autoPlay muted loop playsInline />
        )}
      </div>
      <div className={styles.scrim} aria-hidden />
      <div className={styles.copy}>{children}</div>
    </section>
  );
}
